import {useCallback, useContext, useEffect, useLayoutEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {useTour} from '@reactour/tour';
import Button from '../../../components/bootstrap/Button';
import {HeaderRight} from '../../../layout/Header/Header';
import OffCanvas, {OffCanvasBody, OffCanvasHeader, OffCanvasTitle,} from '../../../components/bootstrap/OffCanvas';
import ThemeContext from '../../../contexts/themeContext';
import showNotification from '../../../components/extras/showNotification';
import useDarkMode from '../../../hooks/useDarkMode';
import Badge from '../../../components/bootstrap/Badge';
import {useMst} from '../../../models';
import NotificationList from './components/NotificationList';
import {observer} from 'mobx-react';
import NotificationSetting from './components/NotificationSetting';
import moment from 'moment';
import 'moment/locale/ko';
import AlarmService from '../../../services/AlarmService';
import {useNavigate} from "react-router-dom";
import AuthService from '../../../services/AuthService';
import {removeItem} from '../../../lib/localstorage';
import {moveLoginPage} from '../../../lib/Util';
import AttendanceHome from '../../../components/Attend/AttendanceHome';
import {useJsApiLoader} from '@react-google-maps/api';
import get from 'lodash/get';
import config from '../../../config';
import PasswordModal from './components/PasswordModal';
import SettingModal from './components/SettingModal';

/******************************************************************
 * 파일명 : frontend2\src\pages\common\Headers\CommonHeaderRight.js
 * 설명 : 상단 util 영역
 ******************************************************************/

// eslint-disable-next-line react/prop-types
const CommonHeaderRight = ({
    beforeChildren = null,
    afterChildren = null,
}) => {
    useJsApiLoader({
    googleMapsApiKey: config.GG_KEY,
})
    const { darkModeStatus, setDarkModeStatus } = useDarkMode();
    const [settingFlag, setSettingFlag] = useState(false);
    const { mobileDesign } = useContext(ThemeContext);
    const styledBtn = {
        color: darkModeStatus ? '#333' : 'light',
        hoverShadow: 'default',
        isLight: !darkModeStatus,
        size: 'lg',
    };

    const [offcanvasStatus, setOffcanvasStatus] = useState(false);
    const [isAttendancePopOpen, setAttendancePopOpen] = useState(false);
    const [isPasswordPopOpen, setIsPasswordPopOpen] = useState(false);
    const [isSettingPopOpen, setIsSettingPopOpen] = useState(false);
    const { i18n } = useTranslation();
    const { user, company, notifiction, attend: attendStore } = useMst();
    const { t } = useTranslation();
    const locationList = get(attendStore, 'locationList', null);

    const allReadHandler = useCallback(async () => {
        await AlarmService.alarmReadAll({ userId: user.id, companyId: company.get.id }).then(response => {
            if (response.data) {
                showNotification(t('notification.alarm'), t('notification.allAlarmRead'), 'info');
                notifiction.getRefresh(company.get.id);
                setOffcanvasStatus(false);
            } else {
                showNotification(t('notification.alarm'), t('notification.failLoadAlarm'), 'danger')
            }
        })
    }, [user, company, notifiction])

    /**
     * Language attribute
     */
    useLayoutEffect(() => {
        document.documentElement.setAttribute('lang', i18n.language);
        moment.locale(i18n.language);
        // setLang(i18n.language);
    });

    useEffect(() => {
        // 헤더는 라우트 전환마다 다시 마운트되므로, 이미 스토어에 값이 있으면 재조회하지 않음
        if (!attendStore?.locationList?.length) {
            attendStore?.getLocationList(async (locations) => {
                // attendStore에 대한 NULL처리
                // 모바일은 출퇴근 팝업을 쓰지 않음(홈 AttendancePunch 카드로 대체) → 자동 오픈 안 함
                if (mobileDesign) return;
                const res = await attendStore?.checkGoToWork?.();
                if (locations.length) {
                    setAttendancePopOpen(res ?? false);
                }
            });
        }
        if (!attendStore?.publicOutIp) {
            attendStore?.getPublicIp();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLogout = async () => {
        AuthService.logout().then(async () => {
            await Promise.all([
                removeItem('access-token'),
                removeItem('refresh-token'),
                removeItem('rootState'),
                removeItem('webToken'),
                removeItem('authUser'),
                user.logout(),
                company.logout(),
            ]).then(() => {
                moveLoginPage();
            });
        });
    };

    return (
        <HeaderRight>
            <div className='row g-3'>
                {beforeChildren}

                {/* TODO:
                This function is only available on mobile
                Remove the condition if you test on desktop */}
                {/* 모바일은 홈 출퇴근 카드(AttendancePunch)로 대체되어 아이콘만 숨김. 로직은 유지 */}
                {!mobileDesign && (
                    <div className='col-auto'>
                        <Button
                            {...styledBtn}
                            onClick={() => {
                                // 출퇴근 타각 대상자인지 먼저 체크
                                if (!user.me.isClock || (mobileDesign && !locationList.length)) {
                                    showNotification(t('common.label.alert'), t('attendance.notEligible'), 'warning');
                                    return;
                                }
                                setAttendancePopOpen(true);
                            }}
                            icon='LockClock'
                        >{t('출퇴근 타각')}
                        </Button>
                    </div>
                )}

                <div className='col-auto'>
                    <Button
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...styledBtn}
                        className='btn_pw'
                        onClick={() => setIsPasswordPopOpen(true)}
                    >{t('비밀번호 변경')}</Button>
                </div>

                <div className='col-auto'>
                    <Button
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...styledBtn}
                        className='btn_setting'
                        icon='GearFill'
                        onClick={() => setIsSettingPopOpen(true)}
                    >{t('설정')}</Button>
                </div>

                {/*	Notifications */}
                <div className='col-auto position-relative'>
                    <Button
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...styledBtn}
                        icon='Notifications'
                        onClick={() => setOffcanvasStatus(true)}
                        aria-label='Notifications'
                        className='btn_noti'
                    >{t('알림')}</Button>
                    {notifiction?.get.badge > 0 && (
                        <Badge color={'danger'} className='position-absolute top-0' style={{right:'5px'}}>
                            {notifiction?.get.badge > 99 ? '99+' : notifiction?.get.badge}
                        </Badge>
                    )}
                </div>

                {/* 로그아웃 */}
                <div className='col-auto'>
                    <Button
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...styledBtn}
                        className='btn_logout'
                        onClick={handleLogout}
                    >
                        {t('로그아웃')}
                    </Button>
                </div>

                {afterChildren}
            </div>

            <OffCanvas id='notificationCanvas' titleId='offcanvasExampleLabel' placement='end' isOpen={offcanvasStatus} setOpen={setOffcanvasStatus}>
                <OffCanvasHeader setOpen={setOffcanvasStatus}>
                    <OffCanvasTitle className='w-100 d-flex justify-content-between' id='offcanvasExampleLabel'>
                        <div>
                            {t('common.label.notifications')} ({notifiction?.get.badge})
                        </div>
                        <div>
                            <Button color='info' size='sm' onClick={() => allReadHandler()}>
                                {t('notification.readAll')}
                            </Button>
                            <Button
                                color='light'
                                icon={!settingFlag ? 'GearFill' : 'Bell'}
                                isOutline={true}
                                className='border-0'
                                onClick={() => {
                                    setSettingFlag(!settingFlag);
                                }}
                            />
                        </div>
                    </OffCanvasTitle>
                </OffCanvasHeader>
                <OffCanvasBody>{settingFlag ? <NotificationSetting /> : <NotificationList toggleNotification={setOffcanvasStatus} />}</OffCanvasBody>
            </OffCanvas>

            {/************* 모달 **************/}
            {/* 출퇴근 타각 */}
            {isAttendancePopOpen && <AttendanceHome setIsOpen={setAttendancePopOpen} isOpen={isAttendancePopOpen} />}
            {/* 비밀번호 변경 */}
            <PasswordModal isPasswordPopOpen={isPasswordPopOpen} setIsPasswordPopOpen={setIsPasswordPopOpen} />
            {/* 설정 */}
            <SettingModal isSettingPopOpen={isSettingPopOpen} setIsSettingPopOpen={setIsSettingPopOpen} />
        </HeaderRight>
    );
};
CommonHeaderRight.propTypes = {
    beforeChildren: PropTypes.node,
    afterChildren: PropTypes.node,
};

export default observer(CommonHeaderRight);
